import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import * as yup from 'yup';

import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { QuantityAndUnitInputValue } from '~components/FormFields/v2/QuantityAndUnitInput';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import { Ticket } from '~hooks/useTickets/models';
import {
  getTicketDatetime,
  getTicketMaterial,
  getTicketNumber,
  getTicketQuantityAndUom,
} from '~utils/tickets/ticket-utils';

export const TICKET_NUMBER_MAX_LENGTH = 64;

export const getTicketSchema = (ticketNumberRequired: boolean) =>
  yup.object().shape({
    ticketNumber: ticketNumberRequired
      ? yup
          .string()
          .trim()
          .min(1, `${t('form_validation_errors.minimum_is_num', { num: 1 })}`)
          .max(
            TICKET_NUMBER_MAX_LENGTH,
            `${t('form_validation_errors.maximum_is_num', { num: TICKET_NUMBER_MAX_LENGTH })}`,
          )
          .required()
      : yup
          .string()
          .trim()
          .max(
            TICKET_NUMBER_MAX_LENGTH,
            `${t('form_validation_errors.maximum_is_num', { num: TICKET_NUMBER_MAX_LENGTH })}`,
          )
          .optional()
          .default(''),
    material: yup
      .object()
      .shape({
        label: yup.string().trim().required(),
        value: yup.string().trim().required(),
      })
      .required(t('form_validation_errors.required_simple')),
    date: yup
      .date()
      .required(t('form_validation_errors.required_simple'))
      .typeError(t('form_validation_errors.invalid_date')),
    time: yup
      .date()
      .required(t('form_validation_errors.required_simple'))
      .typeError(t('form_validation_errors.invalid_time')),
    quantityAndUnit: yup.object<QuantityAndUnitInputValue>().shape({
      quantity: yup
        .number()
        .min(
          0,
          t('form_validation_errors.minimum_field_is_num', {
            field: t('approvals.ticket_details.quantity').toLowerCase(),
            num: 0,
          }),
        )
        .required(
          t('form_validation_errors.required', {
            field: t('approvals.ticket_details.quantity'),
          }),
        )
        .typeError(
          t('form_validation_errors.required', {
            field: t('approvals.ticket_details.quantity'),
          }),
        ),
      uom: yup
        .object()
        .shape({
          label: yup
            .string()
            .trim()
            .required(
              t('form_validation_errors.required', {
                field: t('form_fields.unit'),
              }),
            ),
          value: yup
            .string()
            .trim()
            .required(
              t('form_validation_errors.required', {
                field: t('form_fields.unit'),
              }),
            ),
        })
        .required(t('form_validation_errors.required', { field: t('form_fields.unit') })),
    }),
    loadsNotes: yup.string().trim().nullable(),
    trailerId: yup.string().trim().optional(),
    machineId: yup.string().trim().optional(),
    arrivedPickupTime: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_time')),
    arrivedPickupDate: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_date')),
    arrivedDropoffTime: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_time')),
    arrivedDropoffDate: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_date')),
    loadedTime: yup.date().nullable().typeError(t('form_validation_errors.invalid_time')),
    loadedDate: yup.date().nullable().typeError(t('form_validation_errors.invalid_date')),
    unloadedTime: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_time')),
    unloadedDate: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_date')),
    loadCompletedTime: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_time')),
    loadCompletedDate: yup
      .date()
      .nullable()
      .typeError(t('form_validation_errors.invalid_date')),
  });

export const ticketSchema = getTicketSchema(true);

export type TicketDto = yup.InferType<typeof ticketSchema>;

// Form pickers operate on Date-typed fields but the codebase stores tz-aware dayjs
// values in them (see existing time defaults below), hence the double cast.
function toFormDateTime(datetime: Dayjs | null | undefined) {
  return datetime ? (dayjs.tz(dayjs(datetime)) as unknown as Date) : null;
}

export function setTicketSchemaDefaultValues(ticket?: Ticket, load?: Load, job?: Job) {
  const initialTicketNumber = getTicketNumber(ticket);

  const material = getTicketMaterial(ticket, load, job);
  const initialMaterial = material ? { label: material.name, value: material.id } : null;

  const initialDatetime = getTicketDatetime(ticket, !ticket);

  const quantityAndUom = getTicketQuantityAndUom(ticket, load, job);
  const initialQuantity = quantityAndUom?.quantity;
  const initialUom = quantityAndUom?.uom
    ? { label: startCase(quantityAndUom.uom), value: quantityAndUom.uom }
    : null;

  const defaultValues: TicketDto = {
    ticketNumber: initialTicketNumber || '',
    material: initialMaterial as AutocompleteFieldOption<MaterialTypeahead>,
    date: initialDatetime as unknown as Date,
    time: initialDatetime as unknown as Date,
    quantityAndUnit: {
      quantity: initialQuantity ?? ('' as unknown as number),
      uom: initialUom,
    } as QuantityAndUnitInputValue,
    arrivedPickupTime: toFormDateTime(load?.loadSummary?.arrivedPickupAt),
    arrivedPickupDate: toFormDateTime(load?.loadSummary?.arrivedPickupAt),
    arrivedDropoffTime: toFormDateTime(load?.loadSummary?.arrivedDropoffAt),
    arrivedDropoffDate: toFormDateTime(load?.loadSummary?.arrivedDropoffAt),
    loadedTime: toFormDateTime(load?.loadSummary?.loadedAt),
    loadedDate: toFormDateTime(load?.loadSummary?.loadedAt),
    unloadedTime: toFormDateTime(load?.loadSummary?.unloadedAt),
    unloadedDate: toFormDateTime(load?.loadSummary?.unloadedAt),
    loadCompletedTime: toFormDateTime(load?.loadSummary?.loadCompletedAt),
    loadCompletedDate: toFormDateTime(load?.loadSummary?.loadCompletedAt),
    loadsNotes: load?.notes || '',
    trailerId: ticket?.externalTrailerId ?? '',
    machineId: ticket?.externalMachineId ?? '',
  };

  return defaultValues;
}
